import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useMe } from "../auth/useMe";
import { useRevokeOtherSessions } from "./useRevokeOtherSessions";
import { useRevokeSession } from "./useRevokeSession";
import { useSessions } from "./useSessions";
import { useUpdateRememberSessions } from "./useUpdateRememberSessions";

// Экран «Сессии»: тумблер remember-me + список активных сессий с
// отзывом конкретной и «завершить все кроме текущей» (ADR-0012).
// Read-данные из useMe/useSessions, мутации — отдельными хуками.
export function SessionsSection(): ReactElement {
  const { t } = useTranslation();
  const me = useMe();
  const sessions = useSessions();
  const updateRemember = useUpdateRememberSessions();
  const revokeOne = useRevokeSession();
  const revokeOthers = useRevokeOtherSessions();

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={2}>{t("sessions.rememberTitle")}</Title>
        <Text c="dimmed" size="sm">
          {t("sessions.rememberDescription")}
        </Text>
        {me.isPending ? (
          <Loader />
        ) : me.isError ? (
          <Alert color="red" title={t("sessions.errorHeading")}>
            {t("sessions.loadingError")}
          </Alert>
        ) : (
          <Switch
            label={t("sessions.rememberLabel")}
            checked={me.data.remember_sessions}
            disabled={updateRemember.isPending}
            onChange={(event) => {
              updateRemember.mutate(event.currentTarget.checked);
            }}
          />
        )}
      </Stack>

      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>{t("sessions.activeTitle")}</Title>
          <Button
            variant="light"
            color="red"
            disabled={revokeOthers.isPending}
            onClick={() => {
              revokeOthers.mutate();
            }}
          >
            {t("sessions.revokeOthers")}
          </Button>
        </Group>

        {sessions.isPending ? (
          <Loader />
        ) : sessions.isError ? (
          <Alert color="red" title={t("sessions.errorHeading")}>
            {t("sessions.loadingError")}
          </Alert>
        ) : sessions.data.length === 0 ? (
          <Text c="dimmed">{t("sessions.empty")}</Text>
        ) : (
          <Table withTableBorder withColumnBorders striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("sessions.colDevice")}</Table.Th>
                <Table.Th>{t("sessions.colIp")}</Table.Th>
                <Table.Th>{t("sessions.colLastSeen")}</Table.Th>
                <Table.Th>{t("sessions.colProfile")}</Table.Th>
                <Table.Th>{t("sessions.colActions")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sessions.data.map((session) => (
                <Table.Tr key={session.id}>
                  <Table.Td>
                    <Group gap="xs">
                      <Text size="sm">
                        {session.user_agent ?? t("sessions.unknownDevice")}
                      </Text>
                      {session.current ? (
                        <Badge color="green">
                          {t("sessions.currentBadge")}
                        </Badge>
                      ) : null}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {session.ip ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>{session.last_seen_at}</Table.Td>
                  <Table.Td>
                    <Badge color={session.remember ? "blue" : "gray"}>
                      {t(
                        session.remember
                          ? "sessions.profileRemember"
                          : "sessions.profileShort",
                      )}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      disabled={revokeOne.isPending}
                      onClick={() => {
                        revokeOne.mutate(session.id);
                      }}
                    >
                      {t("sessions.revoke")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Stack>
  );
}
